/**
 * Main game state store for Build the Stack
 *
 * Manages the core game state including placed tiles, connections,
 * grid configuration, and game mode using Zustand.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  Connection,
  GameLevel,
  GameMode,
  GridPosition,
  PlacedTile,
  Tile,
} from '../types'

/**
 * Game state interface extending core functionality
 */
interface GameState {
  // Grid and layout
  /** Size of the game grid */
  gridSize: { width: number; height: number }

  /** Tiles placed on the grid */
  placedTiles: Map<string, PlacedTile>

  /** Connections between tiles */
  connections: Connection[]

  // Game mode and level
  /** Current game mode */
  gameMode: GameMode

  /** Current level being played */
  currentLevel: GameLevel | null

  // UI state
  /** Currently selected tile for interaction */
  selectedTileId: string | null

  /** Tile being dragged from palette */
  draggedTile: Tile | null

  /** Grid position being hovered during drag */
  hoveredPosition: GridPosition | null

  /** Whether game is in edit mode */
  isEditMode: boolean

  /** Whether tutorial is active */
  isTutorialActive: boolean

  // Connection state
  /** Whether connection creation mode is active */
  isConnectionMode: boolean

  /** Source tile ID for connection creation */
  connectionSourceTileId: string | null

  /** Currently selected connection ID */
  selectedConnectionId: string | null

  // Game session
  /** Timestamp when current session started */
  sessionStartTime?: number

  /** Total tiles placed in current session */
  tilesPlacedThisSession: number

  /** Whether the grid has been modified */
  isDirty: boolean
}

/**
 * Game actions interface
 */
interface GameActions {
  // Tile management
  /**
   * Place a tile on the grid at the specified position
   */
  placeTile: (tile: Tile, position: GridPosition) => boolean

  /**
   * Remove a tile from the grid
   */
  removeTile: (tileId: string) => boolean

  /**
   * Move an existing tile to a new position
   */
  moveTile: (tileId: string, newPosition: GridPosition) => boolean

  /**
   * Get tile at specific grid position
   */
  getTileAtPosition: (position: GridPosition) => PlacedTile | null

  // Connection management
  /**
   * Create a connection between two tiles
   */
  createConnection: (fromTileId: string, toTileId: string) => boolean

  /**
   * Remove a connection
   */
  removeConnection: (connectionId: string) => boolean

  /**
   * Get all connections for a specific tile
   */
  getTileConnections: (tileId: string) => Connection[]

  // UI state management
  /**
   * Set the currently selected tile
   */
  setSelectedTile: (tileId: string | null) => void

  /**
   * Set the tile being dragged
   */
  setDraggedTile: (tile: Tile | null) => void

  /**
   * Set the hovered grid position
   */
  setHoveredPosition: (position: GridPosition | null) => void

  /**
   * Toggle edit mode
   */
  toggleEditMode: () => void

  // Connection mode management
  /**
   * Toggle connection creation mode
   */
  toggleConnectionMode: () => void

  /**
   * Set connection source tile
   */
  setConnectionSourceTile: (tileId: string | null) => void

  /**
   * Set selected connection
   */
  setSelectedConnection: (connectionId: string | null) => void

  /**
   * Start connection creation from source tile
   */
  startConnectionFrom: (tileId: string) => void

  /**
   * Complete connection to target tile
   */
  completeConnectionTo: (tileId: string) => boolean

  /**
   * Cancel current connection creation
   */
  cancelConnection: () => void

  // Game management
  /**
   * Start a new game session
   */
  startNewGame: (mode: GameMode, level?: GameLevel) => void

  /**
   * Clear the entire grid
   */
  clearGrid: () => void

  /**
   * Load a level configuration
   */
  loadLevel: (level: GameLevel) => void

  /**
   * Set grid size
   */
  setGridSize: (width: number, height: number) => void

  /**
   * Reset game state to defaults
   */
  resetGameState: () => void

  // Validation
  /**
   * Check if a tile can be placed at position
   */
  canPlaceTile: (tile: Tile, position: GridPosition) => boolean

  /**
   * Validate the current grid configuration
   */
  validateGrid: () => { isValid: boolean; errors: string[]; warnings: string[] }

  /**
   * Check if two tiles can be connected
   */
  canConnect: (fromTileId: string, toTileId: string) => boolean
}

/**
 * Combined game store interface
 */
interface GameStore extends GameState, GameActions {}

/**
 * Default game state values
 */
const defaultGameState: GameState = {
  gridSize: { width: 12, height: 8 },
  placedTiles: new Map(),
  connections: [],
  gameMode: 'tutorial',
  currentLevel: null,
  selectedTileId: null,
  draggedTile: null,
  hoveredPosition: null,
  isEditMode: true,
  isTutorialActive: true,
  isConnectionMode: false,
  connectionSourceTileId: null,
  selectedConnectionId: null,
  tilesPlacedThisSession: 0,
  isDirty: false,
}

/**
 * Game store implementation using Zustand
 */
export const useGameStore = create<GameStore>()(
  persist(
    (set, get) => ({
      ...defaultGameState,

      // Tile management actions
      placeTile: (tile: Tile, position: GridPosition): boolean => {
        const state = get()

        // Basic placement validation
        if (!state.canPlaceTile(tile, position)) {
          return false
        }

        // Create unique tile ID for placement
        const placedTileId = `${tile.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

        // Create placed tile
        const placedTile: PlacedTile = {
          ...tile,
          id: placedTileId, // Use unique ID for placed instance
          position,
          health: 1.0,
          isActive: false,
          placedAt: Date.now(),
          queueLength: 0,
        }

        // Update state
        set(state => ({
          placedTiles: new Map(state.placedTiles).set(placedTileId, placedTile),
          tilesPlacedThisSession: state.tilesPlacedThisSession + 1,
          isDirty: true,
          selectedTileId: placedTileId,
          draggedTile: null, // Clear dragged tile after placement
        }))

        return true
      },

      removeTile: (tileId: string): boolean => {
        const state = get()
        const placedTiles = new Map(state.placedTiles)

        if (!placedTiles.has(tileId)) {
          return false
        }

        // Remove tile
        placedTiles.delete(tileId)

        // Remove all connections involving this tile
        const connections = state.connections.filter(
          conn => conn.fromTileId !== tileId && conn.toTileId !== tileId
        )

        set({
          placedTiles,
          connections,
          isDirty: true,
          selectedTileId:
            state.selectedTileId === tileId ? null : state.selectedTileId,
        })

        return true
      },

      moveTile: (tileId: string, newPosition: GridPosition): boolean => {
        const state = get()
        const tile = state.placedTiles.get(tileId)

        if (!tile || !state.canPlaceTile(tile, newPosition)) {
          return false
        }

        const updatedTile = { ...tile, position: newPosition }

        set(state => ({
          placedTiles: new Map(state.placedTiles).set(tileId, updatedTile),
          isDirty: true,
        }))

        return true
      },

      getTileAtPosition: (position: GridPosition): PlacedTile | null => {
        const { placedTiles } = get()

        for (const tile of placedTiles.values()) {
          if (
            tile.position.x === position.x &&
            tile.position.y === position.y
          ) {
            return tile
          }
        }

        return null
      },

      // Connection management actions
      createConnection: (fromTileId: string, toTileId: string): boolean => {
        const state = get()

        if (!state.canConnect(fromTileId, toTileId)) {
          return false
        }

        const connection: Connection = {
          id: `conn_${fromTileId}_${toTileId}_${Date.now()}`,
          fromTileId,
          toTileId,
          fromPort: 'output',
          toPort: 'input',
          bandwidth: 1000,
          isActive: true,
          path: [], // Will be calculated by rendering system
        }

        set(state => ({
          connections: [...state.connections, connection],
          isDirty: true,
        }))

        return true
      },

      removeConnection: (connectionId: string): boolean => {
        const state = get()
        const connections = state.connections.filter(
          conn => conn.id !== connectionId
        )

        if (connections.length === state.connections.length) {
          return false
        }

        set({ connections, isDirty: true })
        return true
      },

      getTileConnections: (tileId: string): Connection[] => {
        const { connections } = get()
        return connections.filter(
          conn => conn.fromTileId === tileId || conn.toTileId === tileId
        )
      },

      // UI state management
      setSelectedTile: (tileId: string | null) => {
        set({ selectedTileId: tileId })
      },

      setDraggedTile: (tile: Tile | null) => {
        set({ draggedTile: tile })
      },

      setHoveredPosition: (position: GridPosition | null) => {
        set({ hoveredPosition: position })
      },

      toggleEditMode: () => {
        set(state => ({ isEditMode: !state.isEditMode }))
      },

      // Connection mode management
      toggleConnectionMode: () => {
        set(state => ({ isConnectionMode: !state.isConnectionMode }))
      },

      setConnectionSourceTile: (tileId: string | null) => {
        set({ connectionSourceTileId: tileId })
      },

      setSelectedConnection: (connectionId: string | null) => {
        set({ selectedConnectionId: connectionId })
      },

      startConnectionFrom: (tileId: string) => {
        set({ connectionSourceTileId: tileId })
      },

      completeConnectionTo: (tileId: string) => {
        const state = get()
        if (
          !state.connectionSourceTileId ||
          state.connectionSourceTileId === tileId
        ) {
          return false
        }

        const success = state.createConnection(
          state.connectionSourceTileId,
          tileId
        )
        if (success) {
          set({
            connectionSourceTileId: null,
            selectedConnectionId: `conn_${state.connectionSourceTileId}_${tileId}_${Date.now()}`,
          })
        }
        return success
      },

      cancelConnection: () => {
        set({ connectionSourceTileId: null, selectedConnectionId: null })
      },

      // Game management
      startNewGame: (mode: GameMode, level?: GameLevel) => {
        set({
          ...defaultGameState,
          gameMode: mode,
          currentLevel: level || null,
          sessionStartTime: Date.now(),
          isTutorialActive: mode === 'tutorial',
        })
      },

      clearGrid: () => {
        set({
          placedTiles: new Map(),
          connections: [],
          selectedTileId: null,
          connectionSourceTileId: null,
          selectedConnectionId: null,
          isDirty: true,
        })
      },

      loadLevel: (level: GameLevel) => {
        set({
          currentLevel: level,
          gridSize: level.gridSize,
          gameMode: 'level',
          // Clear existing grid when loading level
          placedTiles: new Map(),
          connections: [],
          selectedTileId: null,
          connectionSourceTileId: null,
          selectedConnectionId: null,
          isDirty: false,
        })
      },

      setGridSize: (width: number, height: number) => {
        set({ gridSize: { width, height }, isDirty: true })
      },

      resetGameState: () => {
        set(defaultGameState)
      },

      // Validation methods
      canPlaceTile: (tile: Tile, position: GridPosition): boolean => {
        const state = get()

        // Check grid bounds
        if (
          position.x < 0 ||
          position.x >= state.gridSize.width ||
          position.y < 0 ||
          position.y >= state.gridSize.height
        ) {
          return false
        }

        // Check if position is already occupied
        const existingTile = state.getTileAtPosition(position)
        if (existingTile && existingTile.id !== tile.id) {
          return false
        }

        return true
      },

      validateGrid: () => {
        const { placedTiles, connections } = get()
        const errors: string[] = []
        const warnings: string[] = []

        // Only check for isolated tiles if we have multiple tiles
        if (placedTiles.size > 1) {
          const connectedTiles = new Set<string>()
          connections.forEach(conn => {
            connectedTiles.add(conn.fromTileId)
            connectedTiles.add(conn.toTileId)
          })

          const isolatedTiles = Array.from(placedTiles.keys()).filter(
            tileId => !connectedTiles.has(tileId)
          )

          if (isolatedTiles.length > 0) {
            warnings.push(`${isolatedTiles.length} tile(s) are not connected`)
          }
        }

        // Check for cycles (simplified check)
        // TODO: Implement proper cycle detection algorithm

        return {
          isValid: errors.length === 0,
          errors,
          warnings,
        }
      },

      canConnect: (fromTileId: string, toTileId: string): boolean => {
        const state = get()

        // Can't connect tile to itself
        if (fromTileId === toTileId) {
          return false
        }

        // Check if tiles exist
        const fromTile = state.placedTiles.get(fromTileId)
        const toTile = state.placedTiles.get(toTileId)

        if (!fromTile || !toTile) {
          return false
        }

        // Check if connection already exists
        const existingConnection = state.connections.find(
          conn =>
            (conn.fromTileId === fromTileId && conn.toTileId === toTileId) ||
            (conn.fromTileId === toTileId && conn.toTileId === fromTileId)
        )

        if (existingConnection) {
          return false
        }

        return true
      },
    }),
    {
      name: 'game-store',
      // Only persist essential game state and user preferences
      partialize: state => ({
        gameMode: state.gameMode,
        currentLevel: state.currentLevel,
        gridSize: state.gridSize,
        isTutorialActive: state.isTutorialActive,
        isEditMode: state.isEditMode,
      }),
    }
  )
)
